import React, { useState, useEffect } from 'react';
import { IconCheck, IconSquare } from '@tabler/icons';

// Add custom scrollbar styles
const customScrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = customScrollbarStyles;
  document.head.appendChild(style);
}

const TermMultiSelect = ({
  data = [],
  selectedItems = [],
  onSelectedItemsChange,
  groupKey = 'id',
  groupLabelKey = 'label',
  childrenKey = 'children',
  itemKey = 'id',
  itemLabelKey = 'label',
  placeholder = "အပါတ်စဉ်",
  visible = false,
  onRequestClose,
  onOk,
  maxSelection = 80,
}) => {
  const [internalSelected, setInternalSelected] = useState(selectedItems);
  const [isOpen, setIsOpen] = useState(visible);

  useEffect(() => {
    setInternalSelected(selectedItems);
  }, [selectedItems]);

  useEffect(() => {
    setIsOpen(visible);
  }, [visible]);

  const isGroupSelected = (group) => {
    const children = group[childrenKey] || [];
    return (
      children.length > 0 &&
      children.every(child => internalSelected.includes(child[itemKey]))
    );
  };

  const isItemSelected = (item) => internalSelected.includes(item[itemKey]);

  const handleGroupPress = (group) => {
    const children = group[childrenKey] || [];
    const childIds = children.map(child => child[itemKey]);
    let newSelected;
    if (isGroupSelected(group)) {
      newSelected = internalSelected.filter(id => !childIds.includes(id));
    } else {
      // Check if adding this group would exceed the limit
      const wouldExceedLimit = internalSelected.length + childIds.length > maxSelection;
      if (wouldExceedLimit) {
        // Show warning toast or alert
        alert(`You can only select up to ${maxSelection} items. Please deselect some items first.`);
        return;
      }
      newSelected = Array.from(new Set([...internalSelected, ...childIds]));
    }
    setInternalSelected(newSelected);
    // Don't call onSelectedItemsChange here - only update internal state
  };

  const handleItemPress = (item) => {
    let newSelected;
    if (isItemSelected(item)) {
      newSelected = internalSelected.filter(id => id !== item[itemKey]);
    } else {
      // Check if adding this item would exceed the limit
      if (internalSelected.length >= maxSelection) {
        alert(`You can only select up to ${maxSelection} items. Please deselect some items first.`);
        return;
      }
      newSelected = [...internalSelected, item[itemKey]];
    }
    setInternalSelected(newSelected);
    // Don't call onSelectedItemsChange here - only update internal state
  };

  const handleOk = () => {
    setIsOpen(false);
    if (onSelectedItemsChange) onSelectedItemsChange(internalSelected);
    if (onOk) onOk();
    if (onRequestClose) onRequestClose();
  };

  const handleCancel = () => {
    setIsOpen(false);
    setInternalSelected(selectedItems); // Reset to original selection
    if (onRequestClose) onRequestClose();
  };

  const getSelectedLabels = () => {
    if (internalSelected.length === 0) return placeholder;
    if (internalSelected.length === 1) {
      // Find the selected item label
      for (const group of data) {
        const children = group[childrenKey] || [];
        const item = children.find(child => child[itemKey] === internalSelected[0]);
        if (item) return item[itemLabelKey];
      }
    }
    return `${internalSelected.length} items selected`;
  };

  const renderGroup = (group) => (
    <div key={group[groupKey]} style={{ 
      marginBottom: '16px', 
      border: '1px solid #e5e7eb', 
      borderRadius: '8px', 
      overflow: 'hidden' 
    }}>
      <div 
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px',
          cursor: isGroupSelected(group) ? 'pointer' : internalSelected.length >= maxSelection ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s',
          background: isGroupSelected(group) 
            ? 'linear-gradient(to right, #eff6ff, #e0e7ff)' 
            : internalSelected.length >= maxSelection 
              ? '#f3f4f6'
              : 'linear-gradient(to right, #f9fafb, #f3f4f6)',
          opacity: internalSelected.length >= maxSelection && !isGroupSelected(group) ? 0.6 : 1
        }}
        onClick={() => handleGroupPress(group)}
        onMouseEnter={(e) => {
          if (!isGroupSelected(group) && internalSelected.length < maxSelection) {
            e.target.style.background = 'linear-gradient(to right, #eff6ff, #e0e7ff)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isGroupSelected(group) && internalSelected.length < maxSelection) {
            e.target.style.background = 'linear-gradient(to right, #f9fafb, #f3f4f6)';
          }
        }}
      >
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          width: '20px', 
          height: '20px', 
          marginRight: '12px' 
        }}>
          {isGroupSelected(group) ? (
            <IconCheck style={{ color: '#2563eb' }} size={16} />
          ) : (
            <IconSquare style={{ color: '#9ca3af' }} size={16} />
          )}
        </div>
        <span style={{ 
          fontWeight: '600', 
          fontSize: '14px', 
          color: isGroupSelected(group) ? '#1d4ed8' : '#374151' 
        }}>
          {group[groupLabelKey]}
        </span>
        <span style={{ 
          marginLeft: 'auto', 
          fontSize: '12px', 
          color: '#6b7280', 
          backgroundColor: 'white', 
          padding: '4px 8px', 
          borderRadius: '9999px' 
        }}>
          {(group[childrenKey] || []).length} items
        </span>
      </div>
      <div style={{ backgroundColor: 'white' }}>
        {(group[childrenKey] || []).map((item, index) => (
          <div 
            key={item[itemKey]}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px',
              transition: 'colors 0.15s',
              backgroundColor: isItemSelected(item) 
                ? '#eff6ff' 
                : internalSelected.length >= maxSelection && !isItemSelected(item)
                  ? '#f9fafb'
                  : 'white',
              borderLeft: isItemSelected(item) 
                ? '4px solid #3b82f6' 
                : '4px solid transparent',
              cursor: internalSelected.length >= maxSelection && !isItemSelected(item) 
                ? 'not-allowed' 
                : 'pointer',
              opacity: internalSelected.length >= maxSelection && !isItemSelected(item) ? 0.6 : 1,
              borderBottom: index !== (group[childrenKey] || []).length - 1 ? '1px solid #f3f4f6' : 'none'
            }}
            onClick={() => handleItemPress(item)}
            onMouseEnter={(e) => {
              if (!isItemSelected(item) && !(internalSelected.length >= maxSelection && !isItemSelected(item))) {
                e.target.style.backgroundColor = '#f9fafb';
              }
            }}
            onMouseLeave={(e) => {
              if (!isItemSelected(item) && !(internalSelected.length >= maxSelection && !isItemSelected(item))) {
                e.target.style.backgroundColor = 'white';
              }
            }}
          >
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              width: '16px', 
              height: '16px', 
              marginRight: '12px' 
            }}>
              {isItemSelected(item) ? (
                <IconCheck style={{ color: '#2563eb' }} size={12} />
              ) : (
                <IconSquare style={{ color: '#9ca3af' }} size={12} />
              )}
            </div>
            <span style={{ 
              fontSize: '14px', 
              color: isItemSelected(item) ? '#1d4ed8' : '#4b5563',
              fontWeight: isItemSelected(item) ? '500' : '400'
            }}>
              {item[itemLabelKey]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <div style={{ position: 'relative' }}>
        <div 
          style={{ 
            border: '1px solid #d1d5db', 
            borderRadius: '4px', 
            padding: '8px', 
            cursor: 'pointer', 
            backgroundColor: 'white',
            transition: 'border-color 0.2s'
          }}
          onClick={() => setIsOpen(true)}
          onMouseEnter={(e) => e.target.style.borderColor = '#9ca3af'}
          onMouseLeave={(e) => e.target.style.borderColor = '#d1d5db'}
        >
          <span style={{ color: '#374151' }}>{getSelectedLabels()}</span>
        </div>
      </div>
      
      {isOpen && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          right: 0, 
          bottom: 0, 
          left: 0, 
          backgroundColor: 'rgba(0, 0, 0, 0.6)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          zIndex: 50,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '16px', 
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', 
            maxWidth: '768px', 
            width: '100%', 
            margin: '0 16px', 
            maxHeight: '85vh', 
            overflow: 'hidden', 
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ padding: '24px' }}>
              <div style={{ 
                maxHeight: '384px', 
                overflowY: 'auto', 
                paddingRight: '8px' 
              }} className="custom-scrollbar">
                {data.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '32px 0' }}>
                    <div style={{ color: '#9ca3af', fontSize: '60px', marginBottom: '16px' }}>📋</div>
                    <p style={{ color: '#6b7280', fontSize: '18px' }}>No terms available</p>
                    <p style={{ color: '#9ca3af', fontSize: '14px' }}>Please check back later</p>
                  </div>
                ) : (
                  data.map(renderGroup)
                )}
              </div>
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginTop: '24px', 
                paddingTop: '16px', 
                borderTop: '1px solid #e5e7eb' 
              }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>
                    {internalSelected.length} item{internalSelected.length !== 1 ? 's' : ''} selected
                  </div>
                  {internalSelected.length >= maxSelection && (
                    <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                      Maximum {maxSelection} items allowed
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={handleCancel}
                    style={{
                      padding: '10px 24px',
                      color: '#4b5563',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      transition: 'colors 0.2s',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    ပယ်ဖျက်ပါ
                  </button>
                  <button
                    onClick={handleOk}
                    disabled={internalSelected.length > maxSelection}
                    style={{
                      padding: '10px 24px',
                      borderRadius: '8px',
                      transition: 'all 0.2s',
                      fontWeight: '500',
                      border: 'none',
                      cursor: internalSelected.length > maxSelection ? 'not-allowed' : 'pointer',
                      background: internalSelected.length > maxSelection
                        ? '#d1d5db'
                        : 'linear-gradient(to right, #2563eb, #4f46e5)',
                      color: internalSelected.length > maxSelection ? '#6b7280' : 'white',
                      boxShadow: internalSelected.length > maxSelection ? 'none' : '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                    }}
                    onMouseEnter={(e) => {
                      if (internalSelected.length <= maxSelection) {
                        e.target.style.background = 'linear-gradient(to right, #1d4ed8, #3730a3)';
                        e.target.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (internalSelected.length <= maxSelection) {
                        e.target.style.background = 'linear-gradient(to right, #2563eb, #4f46e5)';
                        e.target.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                  >
                    အတည်ပြုပါ
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TermMultiSelect;